import { useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Typography } from 'antd';

import { FormGroupContext } from '@/components/FormGroup';
import { Modrawer } from '@/components/Modrawer';
import Categories from '@/components/wizard/Categories';
import Files from '@/components/wizard/Files';
import Image from '@/components/wizard/Image';
import ProductDescription from '@/components/wizard/ProductDescription';
import ProductEmail from '@/components/wizard/ProductEmail';
import ProductPaymentMethods from '@/components/wizard/ProductPaymentMethods';
import ProductPrice from '@/components/wizard/ProductPrice';
import ProductTitle from '@/components/wizard/ProductTitle';
import UpgradeSales from '@/components/wizard/UpgradeSales';
import { scrollToElement } from '@/lib/functions';
import { AvailableSearchParams, getSearchParam } from '@/lib/params';
import { PostMessageAction, sendMessage } from '@/lib/postMessage';
import { trpc } from '@/lib/trpc';
import { validateFields } from '@/lib/validation';
import useProductStore, { productStore } from '@/stores/productStore';
import useStepStore, { stepStore } from '@/stores/steps';

import Wizard from '../..';

const Details = () => {
  const { emit } = useContext(FormGroupContext);
  const nextStep = useStepStore(({ nextStep }) => nextStep);
  const navigate = useNavigate();
  const [modrawerIsOpen, setModrawerIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const filesRef = useRef(null);
  const product = useProductStore(d => d);
  const isLocked = useStepStore(d => d.isLocked);

  const storeProduct = trpc.product.storeProduct.useMutation({
    onSuccess: response => {
      if (response.status == 409) {
        if (response.error.title) {
          emit({ type: 'error', payload: { name: 'title', value: response.error.title } });
        }

        if (response.error.description) {
          emit({
            type: 'error',
            payload: { name: 'description', value: response.error.description }
          });
        }

        return;
      }

      if (response.myEduzzResponse?.status !== 201) {
        return;
      }

      productStore.getState().setFields({
        linksWereSent: response.linksWereSent
      });

      stepStore.getState().triggerFinished();

      sendMessage(PostMessageAction.STEPS_COMPLETED, {
        productId: response.product?.id,
        completedAt: new Date().toISOString(),
        isDraft: response.product?.draft
      });

      const isApp = getSearchParam(AvailableSearchParams.APP) === 'true';

      if (!isApp) {
        setTimeout(() => {
          navigate(`/product/${response.product?.id}/created`);
        }, 100);
      }
    }
  });

  const onError = useCallback(() => {
    if (storeProduct.failureCount === 0) {
      return nextStep();
    }
  }, [nextStep, storeProduct]);

  const submit = useCallback(async () => {
    const results = await emit({ type: 'validate' });
    const validationResult = validateFields(results);

    if (!validationResult) {
      return;
    }

    setSaving(true);

    try {
      await storeProduct.mutateAsync(product);

      if (product.payment.type === 'free') {
        productStore.getState().setFields({ upgradeSales: [] });
      }
    } catch (error) {
      onError();
    }

    setSaving(false);
  }, [emit, storeProduct, product, onError, setSaving]);

  return (
    <div className='mt-4 mb-4 flex flex-col gap-4'>
      <div ref={filesRef}>
        <Files atLeastOneFile={false} />
      </div>

      <ProductTitle />
      <ProductDescription />
      <Categories />
      <ProductEmail />
      <Image />
      <ProductPrice />
      {product.payment.type !== 'free' && <ProductPaymentMethods />}
      <UpgradeSales />

      <Modrawer
        open={modrawerIsOpen}
        footerWrapperClassname='flex-col-reverse'
        width={450}
        height={450}
        onCancel={() => setModrawerIsOpen(false)}
        footer={
          <section className='flex flex-1 flex-col gap-2 md:flex-row-reverse'>
            <Button
              type='primary'
              id='btnSendFileModrawer'
              className='flex-1 md:flex-none'
              onClick={() => {
                setModrawerIsOpen(false);
                scrollToElement(filesRef.current);
              }}
            >
              Enviar arquivo
            </Button>

            <Button
              id='btnSaveDraft'
              className='flex-1 md:flex-none'
              onClick={() => {
                submit();
                setModrawerIsOpen(false);
              }}
            >
              Salvar como rascunho
            </Button>
          </section>
        }
        title={'Salvar como rascunho?'}
      >
        <div className='flex w-full flex-col gap-4'>
          <Typography.Title level={5} className='mb-0'>
            Você só poderá começar a vender depois de enviar o arquivo de entrega do produto.
          </Typography.Title>
          <div className='text-sm text-gray-500'>
            Não se preocupe, você pode salvar o progresso e voltar mais tarde.
          </div>
        </div>
      </Modrawer>

      <Wizard.Footer closable preview>
        <Button
          type='primary'
          loading={saving}
          id='submit'
          disabled={isLocked}
          onClick={() => {
            if (product.deliverables.length === 0) {
              setModrawerIsOpen(true);

              return;
            }

            submit();
          }}
        >
          Continuar
        </Button>
      </Wizard.Footer>
    </div>
  );
};

export default Details;
